import React, { useState, useEffect, useRef } from "react";
import { MATCH_ROUNDS } from "../data/matchData";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function medal(pct) {
  if (pct >= 85) return { icon: "🥇", label: "Excellent" };
  if (pct >= 60) return { icon: "🥈", label: "Good" };
  return               { icon: "🥉", label: "Keep practising" };
}

const MedMatchGame = ({ onComplete, userData, saveUserData }) => {
  const [rounds]   = useState(() => shuffle(MATCH_ROUNDS));
  const [roundIdx, setRoundIdx] = useState(0);

  // shuffled side effects for current round (re-shuffle on round change)
  const [shuffledEffects, setShuffledEffects] = useState(() =>
    shuffle(MATCH_ROUNDS[0].pairs)
  );

  // matches: drugId → sideEffectId (what the user linked)
  const [matches,  setMatches]  = useState({});
  // correct: drugId → true/false (null = not yet attempted or cleared)
  const [correct,  setCorrect]  = useState({});
  // wrong flash: drugId → true (cleared after 700ms)
  const [wrongFlash, setWrongFlash] = useState({});

  // drag state
  const [dragging, setDragging] = useState(null); // { drugId, name }
  const [ghostPos, setGhostPos] = useState(null); // { x, y }

  // refs for drop zones: pairId → DOM element
  const dropRefs = useRef({});

  // per-round score
  const [scores, setScores] = useState([]);
  const [phase,  setPhase]  = useState("playing"); // playing | done

  const round      = rounds[roundIdx];
  const isLastRound = roundIdx === rounds.length - 1;
  const correctCount = Object.values(correct).filter(Boolean).length;
  const allMatched   = correctCount === round.pairs.length;

  // Re-shuffle effects whenever round changes
  useEffect(() => {
    setShuffledEffects(shuffle(rounds[roundIdx].pairs));
    setMatches({});
    setCorrect({});
    setWrongFlash({});
    setDragging(null);
    setGhostPos(null);
  }, [roundIdx, rounds]);

  // Global pointer move / up while dragging
  useEffect(() => {
    if (!dragging) return;

    const onMove = (e) => {
      const x = e.clientX ?? e.touches?.[0]?.clientX;
      const y = e.clientY ?? e.touches?.[0]?.clientY;
      if (x !== undefined) setGhostPos({ x, y });
    };

    const onUp = (e) => {
      const x = e.clientX ?? e.changedTouches?.[0]?.clientX;
      const y = e.clientY ?? e.changedTouches?.[0]?.clientY;

      // Find which drop zone contains the pointer
      let hit = null;
      Object.entries(dropRefs.current).forEach(([pairId, el]) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) hit = pairId;
      });

      if (hit) {
        const isCorrect = hit === dragging.drugId;
        setMatches(prev  => ({ ...prev,  [dragging.drugId]: hit }));
        setCorrect(prev  => ({ ...prev,  [dragging.drugId]: isCorrect }));
        if (!isCorrect) {
          setWrongFlash(prev => ({ ...prev, [dragging.drugId]: true }));
          setTimeout(() => {
            setMatches(prev    => { const n = { ...prev };    delete n[dragging.drugId]; return n; });
            setCorrect(prev    => { const n = { ...prev };    delete n[dragging.drugId]; return n; });
            setWrongFlash(prev => { const n = { ...prev };    delete n[dragging.drugId]; return n; });
          }, 700);
        }
      }

      setDragging(null);
      setGhostPos(null);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup",   onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup",   onUp);
    };
  }, [dragging]);

  const startDrag = (e, pair) => {
    if (correct[pair.id]) return; // already locked
    e.preventDefault();
    const x = e.clientX ?? e.touches?.[0]?.clientX;
    const y = e.clientY ?? e.touches?.[0]?.clientY;
    setDragging({ drugId: pair.id, name: pair.drug });
    setGhostPos({ x, y });
  };

  const handleNextRound = () => {
    const roundScore = Object.values(correct).filter(Boolean).length * 20;
    const updated = [...scores, { title: round.title, score: roundScore, max: round.pairs.length * 20 }];
    setScores(updated);

    if (isLastRound) {
      const total    = updated.reduce((s, r) => s + r.score, 0);
      const maxTotal = updated.reduce((s, r) => s + r.max,   0);
      const pct      = Math.round((total / maxTotal) * 100);
      const coins    = Math.max(5,  Math.floor(pct / 5));
      const xp       = Math.max(10, Math.floor(pct / 2));
      if (saveUserData && userData) {
        saveUserData({ ...userData, coins: (userData.coins || 0) + coins, xp: (userData.xp || 0) + xp });
      }
      setPhase("done");
    } else {
      setRoundIdx(i => i + 1);
    }
  };

  // ── Done screen ─────────────────────────────────────────────────────────────
  if (phase === "done") {
    const total    = scores.reduce((s, r) => s + r.score, 0);
    const maxTotal = scores.reduce((s, r) => s + r.max,   0);
    const pct      = Math.round((total / maxTotal) * 100);
    const m        = medal(pct);
    const coins    = Math.max(5,  Math.floor(pct / 5));
    const xp       = Math.max(10, Math.floor(pct / 2));

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50 flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-6">
            <div className="text-6xl mb-2">{m.icon}</div>
            <h2 className="text-2xl font-extrabold text-gray-900">{m.label}</h2>
            <p className="text-gray-500 text-sm mt-1">{rounds.length} rounds completed</p>
          </div>

          <div className="space-y-2 mb-6">
            {scores.map((s, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-2">
                <span className="text-sm text-gray-700 font-medium">{s.title}</span>
                <span className={`text-sm font-bold ${s.score === s.max ? "text-green-600" : "text-amber-600"}`}>
                  {s.score}/{s.max}
                </span>
              </div>
            ))}
          </div>

          <div className="bg-teal-50 border border-teal-200 rounded-2xl p-4 mb-6 text-center">
            <div className="text-3xl font-extrabold text-teal-700">{pct}%</div>
            <div className="text-sm text-teal-600">{total} / {maxTotal} points</div>
            <div className="flex justify-center gap-6 mt-3">
              <div><div className="font-bold text-yellow-600">+{coins} 🪙</div><div className="text-xs text-gray-500">Coins</div></div>
              <div><div className="font-bold text-purple-600">+{xp} ⭐</div><div className="text-xs text-gray-500">XP</div></div>
            </div>
          </div>

          <button
            onClick={onComplete}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-teal-500 to-green-500 text-white font-bold hover:opacity-90 transition-opacity"
          >
            Return to Pharmacy
          </button>
        </div>
      </div>
    );
  }

  // ── Playing screen ──────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50 flex flex-col select-none"
      style={{ touchAction: "none" }}
    >
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <button onClick={onComplete} className="text-sm text-gray-400 hover:text-gray-700">← Leave</button>
        <div className="text-center">
          <div className="font-bold text-gray-800 text-sm">🔗 Side Effects Match</div>
          <div className="text-xs text-gray-400">{round.title}</div>
        </div>
        <span className="text-xs text-gray-400">Round {roundIdx + 1}/{rounds.length}</span>
      </div>

      {/* Round progress */}
      <div className="px-4 pt-3 pb-1 flex gap-1 flex-shrink-0">
        {rounds.map((_, i) => (
          <div key={i} className={`flex-1 h-1.5 rounded-full ${i <= roundIdx ? "bg-teal-500" : "bg-gray-200"}`} />
        ))}
      </div>

      <p className="text-center text-xs text-gray-400 mt-1 mb-2 flex-shrink-0">
        Drag each drug onto its matching side effects
      </p>

      {/* Match area */}
      <div className="flex-1 overflow-y-auto px-3 pb-4">
        <div className="grid grid-cols-2 gap-2 max-w-xl mx-auto">
          {/* Left: drug cards */}
          <div className="space-y-2">
            <div className="text-xs font-bold text-gray-500 uppercase tracking-wide text-center mb-1">Drug</div>
            {round.pairs.map(pair => {
              const isCorrect  = correct[pair.id] === true;
              const isWrong    = wrongFlash[pair.id];
              const isDragging = dragging?.drugId === pair.id;

              let cardCls = "bg-white border-gray-200 text-gray-800";
              if (isCorrect)   cardCls = "bg-green-100 border-green-400 text-green-800";
              else if (isWrong) cardCls = "bg-red-100 border-red-400 text-red-800";
              else if (isDragging) cardCls = "bg-teal-100 border-teal-400 text-teal-800 opacity-60";

              return (
                <div
                  key={pair.id}
                  onPointerDown={(e) => startDrag(e, pair)}
                  className={`border-2 rounded-xl px-3 py-3 text-sm font-bold text-center transition-all ${cardCls} ${
                    !isCorrect ? "cursor-grab active:cursor-grabbing hover:border-teal-400" : "cursor-default"
                  }`}
                  style={{ minHeight: 56 }}
                >
                  {isCorrect && <span className="text-green-600 mr-1">✓</span>}
                  {pair.drug}
                </div>
              );
            })}
          </div>

          {/* Right: side effect drop zones */}
          <div className="space-y-2">
            <div className="text-xs font-bold text-gray-500 uppercase tracking-wide text-center mb-1">Side effects</div>
            {shuffledEffects.map(pair => {
              // Which drug (if any) is correctly matched to this slot?
              const matchedDrugId = Object.entries(correct).find(
                ([dId, ok]) => ok && matches[dId] === pair.id
              )?.[0];
              const isOccupied = !!matchedDrugId;
              const isOver = dragging && !isOccupied; // highlight when drag is active

              return (
                <div
                  key={pair.id}
                  ref={el => { dropRefs.current[pair.id] = el; }}
                  className={`border-2 rounded-xl px-3 py-3 text-xs leading-snug transition-all ${
                    isOccupied
                      ? "bg-green-100 border-green-400 text-green-800"
                      : isOver
                        ? "border-teal-400 bg-teal-50 border-dashed"
                        : "bg-white border-gray-200 text-gray-600 border-dashed"
                  }`}
                  style={{ minHeight: 56 }}
                >
                  {isOccupied
                    ? <><span className="font-bold">✓ {round.pairs.find(p => p.id === matchedDrugId)?.drug}</span><br /><span className="text-green-700">{pair.sideEffect}</span></>
                    : pair.sideEffect
                  }
                </div>
              );
            })}
          </div>
        </div>

        {/* Next button — visible once all matched */}
        {allMatched && (
          <div className="max-w-xl mx-auto mt-4">
            <button
              onClick={handleNextRound}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-teal-500 to-green-500 text-white font-bold hover:opacity-90 transition-opacity"
            >
              {isLastRound ? "Finish →" : "Next round →"}
            </button>
          </div>
        )}
      </div>

      {/* Drag ghost */}
      {dragging && ghostPos && (
        <div
          className="fixed z-50 pointer-events-none bg-teal-600 text-white rounded-xl px-4 py-2 text-sm font-bold shadow-2xl"
          style={{
            left: ghostPos.x - 50,
            top:  ghostPos.y - 20,
            transform: "rotate(-4deg) scale(1.05)",
          }}
        >
          {dragging.name}
        </div>
      )}
    </div>
  );
};

export default MedMatchGame;
